import { AtendimentoPage } from '@/features/atendimento/AtendimentoPage';
import { LoginForm } from '@/features/auth/LoginForm';
import { refreshRequest } from '@/features/auth/api';
import { type Tela, temTela } from '@/features/auth/perfil-de-acesso';
import { CampanhasPage } from '@/features/campanhas/CampanhasPage';
import { CampanhaDetailPage } from '@/features/campanhas/CampanhaDetailPage';
import { ChannelsPage } from '@/features/channels/ChannelsPage';
import { ConnectQrPage } from '@/features/channels/ConnectQrPage';
import { ChatbotPage } from '@/features/chatbot/ChatbotPage';
import { ChatbotEditorPage } from '@/features/chatbot/editor/ChatbotEditorPage';
import { ChatbotExecutionsPage } from '@/features/chatbot/ChatbotExecutionsPage';
import { ConfiguracoesPage } from '@/features/configuracoes/ConfiguracoesPage';
import { ConsultaPage } from '@/features/consulta/ConsultaPage';
import { MensagensAgendadasPage } from '@/features/agendamentos/MensagensAgendadasPage';
import { ContatosPage } from '@/features/contatos/ContatosPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AppShell } from '@/shared/components/layout/AppShell';
import { useAuthStore } from '@/shared/stores/auth-store';
import { Link, Outlet, Router, createRootRoute, createRoute, redirect } from '@tanstack/react-router';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

/** Flag pra evitar toast duplicado quando múltiplas rotas chamam tryRestoreSession
 *  em paralelo (cada beforeLoad). Reseta quando user volta pro login. */
let lastRestoreFailureAt = 0;

async function tryRestoreSession(): Promise<string | null> {
  // Aguarda até 5s pra bootstrap terminar — evita race condition onde main.tsx
  // bootstrapAuthLoop e este tryRestoreSession fazem POST /auth/refresh em
  // paralelo. Refresh tokens são rotacionados a cada uso → 2º request falha
  // com 401 → este retornaria null mesmo com sessão válida → loginRoute não
  // redirecionava pra /atendimento mesmo logado.
  const deadline = Date.now() + 5000;
  while (useAuthStore.getState().bootstrapping && Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 50));
  }

  const state = useAuthStore.getState();
  if (state.accessToken) return state.accessToken;

  // Se passou dos 5s e ainda bootstrapping (Redis off / outage longa),
  // retorna placeholder pra rota carregar com splash. Sem isso, ficaria
  // pendurado eternamente.
  if (state.bootstrapping) return 'bootstrap-pending';

  try {
    const result = await refreshRequest();
    useAuthStore.getState().setSession(result.accessToken, result.user);
    return result.accessToken;
  } catch (err) {
    const isTimeout = err instanceof Error && err.message.includes('timeout');
    if (isTimeout) {
      // Inicia bootstrap loop em background (idempotente — main.tsx pode já ter iniciado).
      useAuthStore.getState().setBootstrapping(true);
      // Retorna placeholder pra rota renderizar splash, NÃO redirect.
      return 'bootstrap-pending';
    }
    // 401-real: refresh expirou. Toast informativo + redirect /login.
    const now = Date.now();
    if (now - lastRestoreFailureAt > 3000) {
      lastRestoreFailureAt = now;
      toast.error('Sessão expirou', {
        description: 'Faça login novamente para continuar.',
        duration: 6000,
      });
    }
    return null;
  }
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: async () => {
    // Se user já tem sessão válida, não faz sentido mostrar tela de login
    // novamente — joga direto pro atendimento. Cobre caso de user digitar
    // /login manual ou ter bookmark antigo. Comportamento simétrico ao
    // authenticatedRoute (que joga pra /login se não-autenticado).
    const token = await tryRestoreSession();
    if (token && token !== 'bootstrap-pending') {
      // biome-ignore lint/suspicious/noExplicitAny: route paths typed dynamically
      throw redirect({ to: '/atendimento' as any });
    }
  },
  component: LoginRoute,
});

function LoginRoute() {
  return (
    <LoginForm
      onSuccess={() => {
        // biome-ignore lint/suspicious/noExplicitAny: route paths typed dynamically — see notes above
        void router.navigate({ to: '/atendimento' as any });
      }}
    />
  );
}

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: async () => {
    const token = await tryRestoreSession();
    if (!token) throw redirect({ to: '/login' });
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

/**
 * Layout pathless por TELA do perfil de acesso: admin passa sempre; atendente
 * passa se o perfil dele libera a tela. Quem cair numa tela sem permissão (URL
 * direta/bookmark) vê "Acesso restrito" — sem redirect. O menu do topo já
 * esconde o que a pessoa não tem; o servidor barra as APIs de verdade.
 */
function rotaDaTela(tela: Tela) {
  return createRoute({
    getParentRoute: () => authenticatedRoute,
    id: `_tela_${tela}`,
    component: () => <TelaGuard tela={tela} />,
  });
}

function TelaGuard({ tela }: { tela: Tela }) {
  const user = useAuthStore((s) => s.user);
  // Enquanto a sessão não hidratou, não renderiza nada — evita flash de "sem
  // permissão" antes do user carregar.
  if (!user) return null;
  if (!temTela(user, tela)) return <AccessDenied />;
  return <Outlet />;
}

const telaDashboard = rotaDaTela('dashboard');
const telaContatos = rotaDaTela('contatos');
const telaHistorico = rotaDaTela('historico');
const telaAgendadas = rotaDaTela('agendadas');
const telaCampanhas = rotaDaTela('campanhas');
const telaCanais = rotaDaTela('canais');
const telaChatbot = rotaDaTela('chatbot');
const telaConfiguracoes = rotaDaTela('configuracoes');

function AccessDenied() {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-sm rounded-lg bg-n-0 p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-danger-50 text-danger-600">
          <Lock size={24} strokeWidth={1.75} aria-hidden="true" />
        </div>
        <h1 className="text-lg font-semibold text-n-900">Acesso restrito</h1>
        <p className="mt-2 text-sm text-n-500">
          Você não tem permissão para acessar esta área. Fale com um administrador se precisar de
          acesso.
        </p>
        <Link
          // biome-ignore lint/suspicious/noExplicitAny: route paths typed dynamically
          to={'/atendimento' as any}
          className="mt-5 inline-flex items-center justify-center rounded-md bg-brand-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600"
        >
          Voltar ao atendimento
        </Link>
      </div>
    </div>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    const token = await tryRestoreSession();
    // biome-ignore lint/suspicious/noExplicitAny: TanStack Router strict path types miss dynamically-added children
    throw redirect({ to: (token ? '/atendimento' : '/login') as any });
  },
});

function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="rounded-lg bg-n-0 p-8 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
        <p className="mt-2 text-sm text-gray-500">
          Tela em desenvolvimento. Próximas stories implementarão os módulos.
        </p>
      </div>
    </div>
  );
}

const placeholderRoutes: ReadonlyArray<readonly [string, string]> = [];

const placeholderTanstackRoutes = placeholderRoutes.map(([path, title]) =>
  createRoute({
    getParentRoute: () => authenticatedRoute,
    path,
    component: () => <PlaceholderPage title={title} />,
  }),
);

// Tela de Canais com funcionalidade real (Story 2.8)
const channelsRoute = createRoute({
  getParentRoute: () => telaCanais,
  path: '/canais',
  component: ChannelsPage,
});

// Tela de pareamento QR Code (canal qr_code/whatsmeow)
const connectQrRoute = createRoute({
  getParentRoute: () => telaCanais,
  path: '/canais/conectar-qr/$numberId',
  component: ConnectQrPage,
});

// Tela de Atendimento (Stories 3.4–3.12)
const atendimentoRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/atendimento',
  component: AtendimentoPage,
});

// Tela de Contatos (Epic 6)
const contatosRoute = createRoute({
  getParentRoute: () => telaContatos,
  path: '/contatos',
  component: ContatosPage,
});

// Tela de Configurações (Epic 7) — `?tab=` na URL persiste aba selecionada
// pra que F5 / share-link voltem na mesma aba.
const configuracoesRoute = createRoute({
  getParentRoute: () => telaConfiguracoes,
  path: '/configuracoes',
  component: ConfiguracoesPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const t = typeof search.tab === 'string' ? search.tab : undefined;
    return t ? { tab: t } : {};
  },
});

// Tela de Consulta (Epic 8)
const consultaRoute = createRoute({
  getParentRoute: () => telaHistorico,
  path: '/consulta',
  component: ConsultaPage,
});

// Relatórios › Mensagens Agendadas — admin ou perfil com a tela (o atendente
// comum agenda pelo campo de mensagem, mas não consulta). O escopo "de todos"
// segue só do admin no servidor; perfil vê as próprias.
const agendamentosRoute = createRoute({
  getParentRoute: () => telaAgendadas,
  path: '/agendamentos',
  component: MensagensAgendadasPage,
});

// Tela de Dashboard (Epic 8)
const dashboardRoute = createRoute({
  getParentRoute: () => telaDashboard,
  path: '/dashboard',
  component: DashboardPage,
});

// Chatbot (Epic 9)
const chatbotListRoute = createRoute({
  getParentRoute: () => telaChatbot,
  path: '/chatbot',
  component: ChatbotPage,
});
const chatbotEditorRoute = createRoute({
  getParentRoute: () => telaChatbot,
  path: '/chatbot/$id/editor',
  component: ChatbotEditorPage,
});
const chatbotExecutionsRoute = createRoute({
  getParentRoute: () => telaChatbot,
  path: '/chatbot/$id/executions',
  component: ChatbotExecutionsPage,
});

// Campanhas (Epic 10)
const campanhasListRoute = createRoute({
  getParentRoute: () => telaCampanhas,
  path: '/campanhas',
  component: CampanhasPage,
});
const campanhaDetailRoute = createRoute({
  getParentRoute: () => telaCampanhas,
  path: '/campanhas/$id',
  component: CampanhaDetailPage,
});

// Push/Eventos: lista + histórico vivem em /configuracoes (abas Integrações).
// Histórico abre como modal overlay dentro da lista — sem rota standalone.
// API externa (chaves + documentação): abas em /configuracoes (Integrações).

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  authenticatedRoute.addChildren([
    // Liberado pra todos os perfis autenticados.
    atendimentoRoute,
    ...placeholderTanstackRoutes,
    // Uma trava por tela do perfil de acesso (admin passa em todas).
    telaCanais.addChildren([channelsRoute, connectQrRoute]),
    telaContatos.addChildren([contatosRoute]),
    telaConfiguracoes.addChildren([configuracoesRoute]),
    telaHistorico.addChildren([consultaRoute]),
    telaAgendadas.addChildren([agendamentosRoute]),
    telaDashboard.addChildren([dashboardRoute]),
    // biome-ignore lint/suspicious/noExplicitAny: TanStack Router types do not align across heterogeneous children with strict optional props
    telaChatbot.addChildren([chatbotListRoute, chatbotEditorRoute, chatbotExecutionsRoute] as any),
    telaCampanhas.addChildren([campanhasListRoute, campanhaDetailRoute]),
    // biome-ignore lint/suspicious/noExplicitAny: TanStack Router types do not align across heterogeneous children with strict optional props
  ] as any),
]);

export const router = new Router({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
